import { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { WizardLegend } from "@similarweb/ui-components";

import CrmEnrichmentJobNameModal from "app/pages/sales-intelligence/modules/crm-enrichment/components/CrmEnrichmentJobNameModal/CrmEnrichmentJobNameModal";
import {
    StyledCrmEnrichmentWizardContainer,
    StyledCrmEnrichmentWizardLegend,
    StyledCrmEnrichmentWizardContent,
    StyledFooterWrapper,
} from "app/pages/sales-intelligence/modules/crm-enrichment/components/styles";
import CrmEnrichmentFieldsMappingWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentFieldsMappingWizardStep";
import CrmEnrichmentJobFiltersWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentJobFiltersWizardStep";
import CrmEnrichmentJobSettingsWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentJobSettingsWizardStep";
import CrmEnrichmentJobTypeWizardStep from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentJobTypeWizardStep";
import CrmEnrichmentWizardNavButtons from "app/pages/sales-intelligence/modules/crm-enrichment/components/wizard/CrmEnrichmentWizardNavButtons";
import { useCrmEnrichmentContext } from "app/pages/sales-intelligence/modules/crm-enrichment/contexts/CrmEnrichmentContext";
import useWizardSteps from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/wizard/useWizardSteps";
import useWizardStepsValidation from "app/pages/sales-intelligence/modules/crm-enrichment/hooks/wizard/useWizardStepsValidation";
import { HS_OBJECTS_OPTIONS } from "app/pages/sales-intelligence/modules/crm-enrichment/impl/hubspot/constants";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import {
    type CrmEnrichmentJobObject,
    type CrmEnrichmentJobType,
    CrmEnrichmentWizardSteps,
    type LeadScoreRefreshDataInterval,
} from "app/pages/sales-intelligence/modules/crm-enrichment/types/types";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import {
    selectHsAccountsColumns,
    selectHsContactsColumns,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspot/exportSelectors";

import useCrmEnrichmentJob from "../../hooks/useCrmEnrichmentJob";
import usePermissionFlow from "../../hooks/usePermissionFlow";
import useHsEnrichmentJobCreateMutation from "../../mutations/useHsEnrichmentJobCreateMutation";
import HsJobReviewWizardStepContainer from "../HsJobReviewWizardStepContainer/HsJobReviewWizardStepContainer";

const CrmEnrichmentJobWizard = () => {
    const [isNameModalOpened, setIsNameModalOpened] = useState(false);

    const { crmProvider, i18nKeys } = useCrmEnrichmentContext();

    const {
        createJobDTO,
        jobName,
        setJobName,
        selectedJobType,
        setSelectedJobType,
        selectedJobObject,
        setSelectedJobObject,
        selectedJobRefreshPeriod,
        setSelectedJobRefreshPeriod,
        selectedEnrichmentSource,
        setSelectedEnrichmentSource,
        selectedLeadScoreRefreshDataInterval,
        setSelectedLeadScoreRefreshDataInterval,
        selectedCountry,
        setSelectedCountry,
        onApplyFiltersChange,
        filters,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
        onResetFilters,
        crmFieldOptions,
        isApplyFiltersSelected,
        mappings,
        setMappings,
        addMapping,
        removeMapping,
        updateMapping,
        resetMappingsForObject,
    } = useCrmEnrichmentJob();

    const accountsColumns = useSelector(selectHsAccountsColumns);
    const contactsColumns = useSelector(selectHsContactsColumns);
    const mappingColumns = selectedJobObject === "company" ? accountsColumns : contactsColumns;

    const { isNextButtonDisabled, isAllPreviousStepsValid } = useWizardStepsValidation({
        selectedJobType,
        selectedJobObject,
        selectedJobRefreshPeriod,
        selectedEnrichmentSource,
        isApplyFiltersSelected,
        filters,
        mappings,
    });

    const { activePageIndex, moveToStep, goNextStep, goPrevStep, legendItems } = useWizardSteps("hubspot");

    const { openPermissionFlow, isActivatedStatus } = usePermissionFlow({ selectedJobType, goNextStep });

    const { mutate, isPending: isLoading, isSuccess } = useHsEnrichmentJobCreateMutation();

    useEffect(() => {
        if (isSuccess) {
            setIsNameModalOpened(false);
            void siNavigator.goToCrmManagementPage({ crm: crmProvider });
        }
    }, [isSuccess]);

    const handleGoNextStep = () => {
        crmEnrichmentTrackingService.wizard.trackClickNext(activePageIndex, crmProvider);

        if (activePageIndex === 0 && !isActivatedStatus) {
            openPermissionFlow();
        } else if (activePageIndex === CrmEnrichmentWizardSteps.ReviewStep) {
            setIsNameModalOpened(true);
        } else {
            goNextStep();
        }
    };

    const handleMoveToStep = (index: CrmEnrichmentWizardSteps) => {
        if (index <= activePageIndex || isAllPreviousStepsValid(index)) {
            moveToStep(index);
        }
    };

    const handleSaveJob = () => {
        crmEnrichmentTrackingService.wizard.trackFinish(jobName, crmProvider);

        mutate(createJobDTO());
    };

    const handleUpdateJobType = (type: CrmEnrichmentJobType) => {
        crmEnrichmentTrackingService.wizard.trackSelectType(type, crmProvider);

        setSelectedJobType(type);
    };

    const handleSelectLeadScoreRefreshDataInterval = (interval: LeadScoreRefreshDataInterval | "") => {
        if (interval) {
            crmEnrichmentTrackingService.wizard.trackSelectLeadScoreRefreshInterval(interval, crmProvider);
        }

        setSelectedLeadScoreRefreshDataInterval(interval);
    };

    const handleUpdateJobObject = (object: CrmEnrichmentJobObject) => {
        crmEnrichmentTrackingService.wizard.trackSelectObject(object, crmProvider);

        setSelectedJobObject(object);
        onResetFilters();
        resetMappingsForObject(object);
    };

    const renderStepContent = () => {
        switch (activePageIndex) {
            case CrmEnrichmentWizardSteps.TypeStep:
                return (
                    <CrmEnrichmentJobTypeWizardStep
                        crmProvider="hubspot"
                        selectedJobType={selectedJobType}
                        onSelectedJobType={handleUpdateJobType}
                    />
                );
            case CrmEnrichmentWizardSteps.SettingsStep:
                return (
                    <CrmEnrichmentJobSettingsWizardStep
                        selectedJobType={selectedJobType}
                        selectedJobObject={selectedJobObject}
                        selectedJobRefreshPeriod={selectedJobRefreshPeriod}
                        selectedLeadScoreRefreshDataInterval={selectedLeadScoreRefreshDataInterval}
                        onSelectJobRefreshPeriod={setSelectedJobRefreshPeriod}
                        onSelectLeadScoreRefreshDataInterval={handleSelectLeadScoreRefreshDataInterval}
                        selectedEnrichmentSource={selectedEnrichmentSource}
                        onSelectEnrichmentSource={setSelectedEnrichmentSource}
                        selectedCountry={selectedCountry}
                        translationKeys={i18nKeys}
                        jobObjectOptions={HS_OBJECTS_OPTIONS}
                        onSelectJobObject={handleUpdateJobObject}
                        onSelectCountry={setSelectedCountry}
                    />
                );
            case CrmEnrichmentWizardSteps.FiltersStep:
                return (
                    <CrmEnrichmentJobFiltersWizardStep
                        isApplyFiltersSelected={isApplyFiltersSelected}
                        onApplyFiltersChange={onApplyFiltersChange}
                        filters={filters}
                        onAddFilter={onAddFilter}
                        onDeleteFilter={onDeleteFilter}
                        onFilterChange={onFilterChange}
                        onCrmFieldFilterChange={onCrmFieldFilterChange}
                        crmFieldOptions={crmFieldOptions}
                        selectedJobObject={selectedJobObject}
                    />
                );
            case CrmEnrichmentWizardSteps.FieldsMappingStep:
                return (
                    <CrmEnrichmentFieldsMappingWizardStep
                        crmProvider="hubspot"
                        enrichmentObject={selectedJobObject as CrmEnrichmentJobObject}
                        jobType={selectedJobType}
                        mappings={mappings}
                        columns={mappingColumns}
                        isColumnsLoading={false}
                        onAddMapping={addMapping}
                        onRemoveMapping={removeMapping}
                        onUpdateMapping={updateMapping}
                    />
                );
            case CrmEnrichmentWizardSteps.ReviewStep:
                return (
                    <HsJobReviewWizardStepContainer
                        selectedJobType={selectedJobType}
                        selectedJobObject={selectedJobObject}
                        selectedJobRefreshPeriod={selectedJobRefreshPeriod}
                        selectedEnrichmentSource={selectedEnrichmentSource}
                        selectedCountry={selectedCountry}
                        filters={filters}
                        isApplyFiltersSelected={!!isApplyFiltersSelected}
                        mappingsCount={mappings.length}
                        mappings={mappings}
                        onMoveToStep={handleMoveToStep}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <StyledCrmEnrichmentWizardContainer>
            <StyledCrmEnrichmentWizardLegend>
                <WizardLegend
                    items={legendItems}
                    onItemClickFunc={handleMoveToStep}
                />
            </StyledCrmEnrichmentWizardLegend>
            <StyledCrmEnrichmentWizardContent
                $isFillHeight={activePageIndex === CrmEnrichmentWizardSteps.FieldsMappingStep}
            >
                {renderStepContent()}
            </StyledCrmEnrichmentWizardContent>
            <StyledFooterWrapper>
                <CrmEnrichmentWizardNavButtons
                    onBackStepClick={goPrevStep}
                    onNextStepClick={handleGoNextStep}
                    isNextDisabled={isNextButtonDisabled(activePageIndex)}
                    isLoading={isLoading}
                    activePageIndex={activePageIndex}
                    numberOfPages={legendItems.length}
                />
            </StyledFooterWrapper>
            <CrmEnrichmentJobNameModal
                crmProvider="hubspot"
                isOpened={isNameModalOpened}
                onClose={() => setIsNameModalOpened(false)}
                jobName={jobName}
                onChangeJobName={setJobName}
                isSubmitDisabled={!jobName.trim()}
                isSubmitLoading={isLoading}
                onSaveName={handleSaveJob}
            />
        </StyledCrmEnrichmentWizardContainer>
    );
};

export default CrmEnrichmentJobWizard;
